import React, { useState, useEffect, useRef } from 'react';
import { API } from '../utils/api';
import axios from 'axios';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';
import SiteMapPreview from './SiteMapPreview';
import { MapPin, Calculator, Building2, User, Settings, Eye, Loader2, AlertCircle, CheckCircle2, Camera, X } from 'lucide-react';
import {
  SITE_COUNTRY,
  WORLD_COUNTRIES,
  validatePostcode,
  getEmptySite,
  getGeocodeErrorMessage,
  validateEmail
} from '../utils/siteFormUtils';

const AddSiteModal = ({ isOpen, onClose, onSiteCreated }) => {
  const [newSite, setNewSite] = useState(getEmptySite());
  
  // Thumbnail state
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const thumbnailInputRef = useRef(null);
  
  // Clients list for dropdown
  const [clients, setClients] = useState([]);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ first_name: '', last_name: '', email: '', company_name: '' });
  const [savingClient, setSavingClient] = useState(false);
  
  // Geocoding state
  const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const [hasCalculatedCoordinates, setHasCalculatedCoordinates] = useState(false);
  
  // Track address for change detection (using ref to avoid re-render loops)
  const addressSnapshotRef = useRef('');
  
  // Map key to force re-render on new coordinates
  const [mapKey, setMapKey] = useState(0);
  
  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Modal states
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset form when modal opens
      setNewSite(getEmptySite());
      setCoordinates({ latitude: null, longitude: null });
      setHasCalculatedCoordinates(false);
      setGeocodeError('');
      addressSnapshotRef.current = '';
      setMapKey(0);
      // Reset thumbnail
      setThumbnailFile(null);
      setThumbnailPreview(null);
      // Reset inline client form
      setShowNewClientForm(false);
      setNewClientForm({ first_name: '', last_name: '', email: '', company_name: '' });
      // Fetch clients
      axios.get(`${API}/quoting/clients`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(res => setClients(res.data.clients || []))
        .catch(() => setClients([]));
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Compress and resize image before upload
  const compressImage = (file, maxWidth = 1000, maxHeight = 1000, quality = 1.0) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          
          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle thumbnail file selection
  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrorModal({ show: true, message: 'Please select an image file (JPG, PNG, etc.).' });
        return;
      }
      
      // Compress large images (over 500KB)
      let processedFile = file;
      if (file.size > 500 * 1024) {
        processedFile = await compressImage(file);
      }
      
      setThumbnailFile(processedFile);
      const reader = new FileReader();
      reader.onload = (e) => setThumbnailPreview(e.target.result);
      reader.readAsDataURL(processedFile);
    }
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  // Build current address string
  const getCurrentAddressString = () => {
    return `${newSite.address_line_1}|${newSite.address_line_2}|${newSite.city}|${newSite.postcode}`;
  };

  // Handle address field changes - clear coordinates if address changed after calculation
  const handleAddressChange = (field, value) => {
    const updatedSite = { ...newSite, [field]: value };
    setNewSite(updatedSite);
    
    // Check if coordinates were calculated and address has changed
    if (hasCalculatedCoordinates) {
      const newAddressString = `${updatedSite.address_line_1}|${updatedSite.address_line_2}|${updatedSite.city}|${updatedSite.postcode}`;
      if (newAddressString !== addressSnapshotRef.current) {
        setCoordinates({ latitude: null, longitude: null });
        setHasCalculatedCoordinates(false);
        setGeocodeError('');
      }
    }
  };

  // Check if required address fields are filled
  const areRequiredAddressFieldsFilled = () => {
    return (
      newSite.address_line_1.trim() !== '' &&
      newSite.city.trim() !== '' &&
      newSite.postcode.trim() !== '' &&
      validatePostcode(newSite.postcode)
    );
  };

  // Check if all required fields are filled (for submit button)
  const areAllRequiredFieldsFilled = () => {
    return (
      newSite.site_id.trim() !== '' &&
      newSite.site_name.trim() !== '' &&
      areRequiredAddressFieldsFilled() &&
      hasCalculatedCoordinates &&
      coordinates.latitude !== null &&
      coordinates.longitude !== null
    );
  };

  // Handle geocoding with animation
  const handleCalculateCoordinates = async () => {
    setIsGeocoding(true);
    setGeocodeError('');
    
    try {
      const response = await axios.post(`${API}/admin/sites/geocode`, {
        address_line_1: newSite.address_line_1.trim(),
        address_line_2: newSite.address_line_2.trim(),
        city: newSite.city.trim(),
        postcode: newSite.postcode.trim().toUpperCase(),
        country: SITE_COUNTRY
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 15000
      });
      
      setCoordinates({
        latitude: response.data.latitude,
        longitude: response.data.longitude
      });
      setHasCalculatedCoordinates(true);
      addressSnapshotRef.current = getCurrentAddressString();
      // Force map to re-render with new key to reset zoom/position
      setMapKey(prev => prev + 1);
      
    } catch (error) {
      console.error('Geocoding error:', error);
      const errorMessage = getGeocodeErrorMessage(error);
      setGeocodeError(errorMessage);
      setCoordinates({ latitude: null, longitude: null });
      setHasCalculatedCoordinates(false);
    } finally {
      setIsGeocoding(false);
    }
  };

  // Get full address for display
  const getFullAddress = () => {
    const parts = [newSite.address_line_1, newSite.address_line_2, newSite.city, newSite.postcode, SITE_COUNTRY];
    return parts.filter(p => p && p.trim()).join(', ');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!areAllRequiredFieldsFilled()) {
      setErrorModal({
        show: true,
        message: 'Please fill all required fields and calculate site coordinates before creating the site.'
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/admin/sites`, {
        site_id: newSite.site_id.trim(),
        site_name: newSite.site_name.trim(),
        description: newSite.description,
        address_line_1: newSite.address_line_1.trim(),
        address_line_2: newSite.address_line_2,
        city: newSite.city.trim(),
        postcode: newSite.postcode.trim().toUpperCase(),
        country: SITE_COUNTRY,
        client_id: newSite.client_id || null,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const createdSite = response.data;
      
      // Upload thumbnail if provided
      let thumbnailUploadFailed = false;
      if (thumbnailFile) {
        try {
          const formData = new FormData();
          formData.append('file', thumbnailFile);
          await axios.post(`${API}/admin/sites/${createdSite.site_id}/thumbnail`, formData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (thumbnailError) {
          console.error('Failed to upload thumbnail:', thumbnailError);
          thumbnailUploadFailed = true;
        }
      }
      
      if (thumbnailUploadFailed) {
        setSuccessModal({
          show: true,
          message: 'Site created successfully, but thumbnail upload failed. You can add the thumbnail later by editing the site.'
        });
      } else {
        setSuccessModal({
          show: true,
          message: 'Site created successfully!'
        });
      }
      onSiteCreated && onSiteCreated(createdSite);
      onClose();
    } catch (error) {
      console.error('Error creating site:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create site. Please try again.';
      setErrorModal({
        show: true,
        message: `Error: ${errorMessage}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return (
    <>
      <SuccessModal 
        isOpen={successModal.show} 
        onClose={() => setSuccessModal({ show: false, message: '' })} 
        message={successModal.message} 
      />
      <ErrorModal 
        isOpen={errorModal.show} 
        onClose={() => setErrorModal({ show: false, message: '' })} 
        message={errorModal.message} 
      />
    </>
  );

  return (
    <>
      <SuccessModal 
        isOpen={successModal.show} 
        onClose={() => setSuccessModal({ show: false, message: '' })} 
        message={successModal.message} 
      />
      <ErrorModal 
        isOpen={errorModal.show} 
        onClose={() => setErrorModal({ show: false, message: '' })} 
        message={errorModal.message} 
      />

      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm"></div>
          
          <div className="relative bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl border border-slate-700 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Add New Site</h3>
                    <p className="text-sm text-gray-400">Create a new construction site</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg disabled:opacity-50"
                  data-testid="close-modal-btn"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Site Information */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  Site Information
                </h4>
                
                {/* Two column layout: 30% thumbnail, 70% form fields */}
                <div className="flex gap-6">
                  {/* Left Column - Thumbnail (30%) */}
                  <div className="w-[30%] flex-shrink-0">
                    <div className="flex flex-col items-center">
                      {/* Thumbnail Preview - Large */}
                      <div className="relative w-full aspect-square max-w-[180px]">
                        <input
                          ref={thumbnailInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailChange}
                          className="hidden"
                          id="thumbnail-upload"
                          data-testid="thumbnail-input"
                        />
                        {thumbnailPreview ? (
                          <div className="relative w-full h-full">
                            <img
                              src={thumbnailPreview}
                              alt="Site thumbnail preview"
                              className="w-full h-full rounded-xl object-cover border-2 border-slate-600"
                            />
                            <button
                              type="button"
                              onClick={removeThumbnail}
                              className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg transition-colors"
                              data-testid="remove-thumbnail-btn"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label
                            htmlFor="thumbnail-upload"
                            className="w-full h-full bg-slate-700 rounded-xl border-2 border-dashed border-slate-500 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 hover:bg-slate-600/50 transition-colors"
                          >
                            <Camera className="w-10 h-10 text-slate-400 mb-2" />
                            <span className="text-xs text-slate-400">Upload thumbnail</span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column - Form Fields (70%) */}
                  <div className="flex-1">
                    {/* Site ID and Site Name on same row */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Site ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., SITE-001"
                          value={newSite.site_id}
                          onChange={(e) => setNewSite({...newSite, site_id: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                          data-testid="site-id-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Site Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Downtown Office Complex"
                          value={newSite.site_name}
                          onChange={(e) => setNewSite({...newSite, site_name: e.target.value})}
                          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                          data-testid="site-name-input"
                          required
                        />
                      </div>
                    </div>
                    
                    {/* Description below */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Description <span className="text-gray-500">(Optional)</span>
                      </label>
                      <textarea
                        placeholder="Brief description of the site, location, or project details..."
                        value={newSite.description}
                        onChange={(e) => setNewSite({...newSite, description: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none"
                        rows={3}
                        data-testid="site-description-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Site Address Section */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  Site Address
                  <span className="ml-2 text-xs text-amber-400 font-normal">(Required for location features)</span>
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Address Line 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 123 High Street"
                      value={newSite.address_line_1}
                      onChange={(e) => handleAddressChange('address_line_1', e.target.value)}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      data-testid="address-line-1-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Address Line 2 <span className="text-gray-500">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Unit 5, Business Park"
                      value={newSite.address_line_2}
                      onChange={(e) => handleAddressChange('address_line_2', e.target.value)}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      data-testid="address-line-2-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Town / City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., London"
                        value={newSite.city}
                        onChange={(e) => handleAddressChange('city', e.target.value)}
                        className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                        data-testid="city-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Postcode <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., SW1A 1AA"
                        value={newSite.postcode}
                        onChange={(e) => handleAddressChange('postcode', e.target.value.toUpperCase())}
                        className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors ${
                          newSite.postcode && !validatePostcode(newSite.postcode) 
                            ? 'border-red-500' 
                            : 'border-slate-600'
                        }`}
                        data-testid="postcode-input"
                        required
                      />
                      {newSite.postcode && !validatePostcode(newSite.postcode) && (
                        <p className="text-red-400 text-xs mt-1 flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Invalid UK postcode format (e.g., SW1A 1AA, M1 1AE)
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      value={SITE_COUNTRY}
                      disabled
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-gray-400 cursor-not-allowed"
                      data-testid="country-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Site addresses are UK only</p>
                  </div>
                </div>
              </div>

              {/* Site Location Geo-coding Section */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <Calculator className="w-4 h-4 text-white" />
                  </div>
                  Site Location Geo-coding
                  <span className="ml-2 text-xs text-amber-400 font-normal">(Required)</span>
                </h4>
                
                {/* Calculate button with animation */}
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={handleCalculateCoordinates}
                    disabled={!areRequiredAddressFieldsFilled() || isGeocoding}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all duration-300 transform ${
                      areRequiredAddressFieldsFilled() && !isGeocoding
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                        : 'bg-slate-700 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="calculate-coordinates-btn"
                  >
                    {isGeocoding ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="animate-pulse">Calculating...</span>
                      </>
                    ) : (
                      <>
                        <Calculator className={`w-5 h-5 ${areRequiredAddressFieldsFilled() ? 'animate-bounce' : ''}`} />
                        {hasCalculatedCoordinates ? 'Recalculate Coordinates' : 'Calculate Site Coordinates'}
                      </>
                    )}
                  </button>
                  {!areRequiredAddressFieldsFilled() && (
                    <p className="text-gray-500 text-xs mt-2 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Complete all required address fields to enable coordinate calculation
                    </p>
                  )}
                </div>
                
                {/* Geocode error */}
                {geocodeError && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg animate-shake">
                    <p className="text-red-300 text-sm flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {geocodeError}
                    </p>
                  </div>
                )}
                
                {/* Coordinate fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Latitude
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={coordinates.latitude !== null ? coordinates.latitude.toFixed(6) : ''}
                        readOnly
                        placeholder="Will be calculated"
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-gray-300 cursor-not-allowed"
                        data-testid="latitude-input"
                      />
                      {hasCalculatedCoordinates && coordinates.latitude !== null && (
                        <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-400 animate-scale-in" />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Longitude
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={coordinates.longitude !== null ? coordinates.longitude.toFixed(6) : ''}
                        readOnly
                        placeholder="Will be calculated"
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-gray-300 cursor-not-allowed"
                        data-testid="longitude-input"
                      />
                      {hasCalculatedCoordinates && coordinates.longitude !== null && (
                        <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-400 animate-scale-in" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Map preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Location Preview
                  </label>
                  <SiteMapPreview 
                    key={mapKey}
                    latitude={coordinates.latitude} 
                    longitude={coordinates.longitude}
                    height="200px"
                    siteName={newSite.site_name || 'New Site'}
                  />
                </div>
              </div>

              {/* Client Section */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  Client
                </h4>
                {!showNewClientForm ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Select Client <span className="text-gray-500">(Optional)</span>
                    </label>
                    <select
                      value={newSite.client_id || ''}
                      onChange={(e) => setNewSite({...newSite, client_id: e.target.value || null})}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      data-testid="client-select"
                    >
                      <option value="">-- No client linked --</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.first_name} {c.last_name}{c.company_name ? ` (${c.company_name})` : ''}{c.email ? ` - ${c.email}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewClientForm(true)}
                      className="mt-3 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      data-testid="new-client-inline-btn"
                    >
                      + Create New Client
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">First Name *</label>
                        <input type="text" value={newClientForm.first_name} onChange={(e) => setNewClientForm({...newClientForm, first_name: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="inline-client-first-name" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Last Name *</label>
                        <input type="text" value={newClientForm.last_name} onChange={(e) => setNewClientForm({...newClientForm, last_name: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="inline-client-last-name" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                        <input type="email" value={newClientForm.email} onChange={(e) => setNewClientForm({...newClientForm, email: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="inline-client-email" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">Company Name</label>
                        <input type="text" value={newClientForm.company_name} onChange={(e) => setNewClientForm({...newClientForm, company_name: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-testid="inline-client-company" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="button"
                        disabled={savingClient || !newClientForm.first_name.trim() || !newClientForm.last_name.trim() || !newClientForm.email.trim()}
                        onClick={async () => {
                          setSavingClient(true);
                          try {
                            const res = await axios.post(`${API}/quoting/clients`, newClientForm, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                            const created = res.data;
                            setClients(prev => [...prev, created]);
                            setNewSite(prev => ({...prev, client_id: created.id}));
                            setShowNewClientForm(false);
                            setNewClientForm({ first_name: '', last_name: '', email: '', company_name: '' });
                          } catch (err) { alert(err.response?.data?.detail || 'Failed to create client'); }
                          finally { setSavingClient(false); }
                        }}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 text-white rounded-lg text-sm transition-colors"
                        data-testid="inline-client-save"
                      >
                        {savingClient ? 'Saving...' : 'Save Client'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowNewClientForm(false); setNewClientForm({ first_name: '', last_name: '', email: '', company_name: '' }); }}
                        className="text-sm text-slate-400 hover:text-white transition-colors"
                        data-testid="inline-client-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Site Configuration Section */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <Settings className="w-4 h-4 text-white" />
                  </div>
                  Site Configuration
                </h4>
                <div className="flex items-center justify-between p-4 bg-slate-700 rounded-lg border border-slate-600">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${
                      newSite.status === 'active' 
                        ? 'bg-green-500/20 border border-green-500/30' 
                        : 'bg-slate-600 border border-slate-500'
                    }`}>
                      {newSite.status === 'active' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className={`font-medium ${newSite.status === 'active' ? 'text-green-300' : 'text-gray-300'}`}>
                        {newSite.status === 'active' ? 'Active' : 'Inactive'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {newSite.status === 'active' ? 'Users can clock in to this site' : 'Users cannot clock in to this site'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewSite({...newSite, status: newSite.status === 'active' ? 'inactive' : 'active'})}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                      newSite.status === 'active' ? 'bg-green-500' : 'bg-slate-600'
                    }`}
                    data-testid="status-toggle"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
                        newSite.status === 'active' ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Site Preview Section */}
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                    <Eye className="w-4 h-4 text-white" />
                  </div>
                  Site Preview
                </h4>
                <div className="bg-slate-700 rounded-lg p-4 border border-slate-600">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-1">
                        <h4 className="font-medium text-white truncate">
                          {newSite.site_id || 'Site ID'}
                        </h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                          newSite.status === 'active' 
                            ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                            : 'bg-slate-600 text-gray-300 border border-slate-500'
                        }`}>
                          {newSite.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-white mb-1">
                        {newSite.site_name || 'Site Name'}
                      </p>
                      <p className="text-sm text-gray-400">
                        {newSite.description || 'No description provided'}
                      </p>
                      {getFullAddress() && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 font-medium">Site Address:</p>
                          <p className="text-xs text-gray-400">{getFullAddress()}</p>
                          {hasCalculatedCoordinates ? (
                            <div className="text-xs text-green-400 flex items-center mt-1">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Coordinates calculated - All location features enabled
                            </div>
                          ) : (
                            <div className="text-xs text-amber-400 flex items-center mt-1">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Coordinates required - Click &quot;Calculate Site Coordinates&quot;
                            </div>
                          )}
                        </div>
                      )}
                      {newSite.client_id && (() => {
                        const c = clients.find(cl => cl.id === newSite.client_id);
                        return c ? (
                          <div className="mt-2 border-t border-slate-600 pt-2">
                            <p className="text-xs text-gray-500 font-medium">Client:</p>
                            <p className="text-xs font-medium text-gray-300">{c.first_name} {c.last_name}</p>
                            {c.email && <p className="text-xs text-gray-400">{c.email}</p>}
                            {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-slate-700">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors font-medium disabled:opacity-50"
                  data-testid="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !areAllRequiredFieldsFilled()}
                  className={`px-8 py-3 rounded-lg font-medium shadow-lg transition-all duration-200 ${
                    areAllRequiredFieldsFilled() && !isSubmitting
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-700 text-white hover:from-purple-700 hover:to-indigo-800 hover:shadow-xl'
                      : 'bg-slate-600 text-gray-400 cursor-not-allowed'
                  }`}
                  data-testid="create-site-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Creating...
                    </span>
                  ) : (
                    'Create Site'
                  )}
                </button>
              </div>
              
              {!areAllRequiredFieldsFilled() && (
                <p className="text-center text-gray-500 text-xs">
                  Complete all required fields and calculate coordinates to enable site creation
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Add CSS for animations */}
      <style>{`
        @keyframes scale-in {
          0% { transform: scale(0) translateY(-50%); opacity: 0; }
          100% { transform: scale(1) translateY(-50%); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </>
  );
};

export default AddSiteModal;
